import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Slider from "@mui/material/Slider";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import moment from "moment";
import { colors } from "../../../../styles";
import { formatCurrency } from "../../../../helpers/currency";
import { formatDate } from "../../../../helpers/dates";
import {
  isEnrolledInSponsoredSavings,
  calculateGoalProgress,
} from "../../../../helpers/matches";
import { useContainerDimensions, useIsMobile } from "../../../../helpers/ui";
import {
  ProgramStatusTypeT,
  SponsoredSavings,
  QueryAccountResponse,
  ChartTypePeriod,
} from "../../../../types";
import { ReactComponent as EarnedIcon } from "../../../../assets/icons/earned.svg";
import { ReactComponent as ArrowDown } from "../../../../assets/arrowdown.svg";
import { ReactComponent as ArrowRight } from "../../../../assets/arrowright.svg";
import { ReactComponent as DebitCardImg } from "../../../../assets/images/debitcard.svg";
import { ReactComponent as MailIcon } from "../../../../assets/icons/mail.svg";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import { ReactComponent as TodayTooltip } from "../../../../assets/today-tooltip.svg";
import { ReactComponent as MyAccountIcon } from "../../../../assets/icons/employeeDashboard/account.svg";
import { ReactComponent as TransfersIcon } from "../../../../assets/icons/employeeDashboard/transfers.svg";
import { ReactComponent as SettingsIcon } from "../../../../assets/icons/employeeDashboard/settings.svg";
import { ReactComponent as BackIcon } from "../../../../assets/arrowleft.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";
import TifinESALogo from "../../../../assets/logos/tifinEsa.svg";
import Transaction from "../../../../components/Transaction";
import T from "../../../../components/Typography";
import {
  SectionFlexColumn,
  SectionFlexRow,
} from "../../../../components/General";
import Button from "../../../../components/Button";
import useGetAccountBalances from "../../../../hooks/queries/employee/useGetAccountBalances";
import {
  MenuRow,
  RowLabel,
  Divider,
  MenuSelected,
} from "../../../../components/AdminNavBar";
import {
  EMPLOYEE_PARENT_ROUTE,
  EMPLOYEE_ACCOUNT,
  EMPLOYEE_TRANSFERS,
  employeeAccounInviteCoworkersPath,
  employeeAccountPath,
  employeeActivateCardPath,
  employeeDebitCardInfoPath,
  employeeChangeContributionPath,
  employeeTransactionsPath,
} from "../../../../routes/paths";
import {
  cardIsActivated,
  hasDebitCard,
  showDebitCardFeature,
} from "../../../../helpers/debitcard";

export const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  background: colors.off_white,
  boxSizing: "border-box",
});

const menuItems = [
  {
    label: "My Account",
    link: "",
    icon: MyAccountIcon,
  },
  {
    label: "Transfers",
    link: "transfers",
    icon: TransfersIcon,
  },
  {
    label: "Profile & Settings",
    link: "settings",
    icon: SettingsIcon,
  },
];

export const SideNav = () => {
  const [activePage, setActivePage] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const splitPath = location.pathname.split(
      `/${EMPLOYEE_PARENT_ROUTE}/${EMPLOYEE_ACCOUNT}/`
    );

    if (splitPath?.length === 1) {
      // account route
      setActivePage(menuItems[0]);
      return;
    }

    const matchedItem = menuItems.filter((item) => {
      if (!splitPath?.[1].includes("/")) {
        // check if the path matches a defined menu item line "transfer"
        const menuItemMatches = menuItems.filter((nestedItem) => {
          if (nestedItem.link === splitPath?.[1]) {
            return true;
          }
          return false;
        });

        if (!menuItemMatches.length) {
          // this is a nested "account" route
          return true;
        }
      }
      // check for a nested route for a non "account" path
      if (item.link === "") return false;

      // check for exact match
      if (splitPath?.[1] === item.link) {
        return true;
      }
      // check for match with trailing slash to include subroutes
      if (splitPath?.[1].includes(`${item.link}/`)) {
        return true;
      }
      return false;
    });

    if (matchedItem) {
      const newActivePage = matchedItem?.[0];
      setActivePage(newActivePage);
    }
  }, [location.pathname]);

  const goToPage = (newLink: string) => {
    navigate(newLink);
  };

  const SideNavContainer = styled("div")({
    height: "100vh",
    borderRight: `1px solid ${colors.gray_01}`,
    minWidth: 240,
    width: 240,
    display: "flex",
    flexDirection: "column",
  });

  return (
    <SideNavContainer>
      <SectionFlexColumn style={{ width: "80%", margin: "0 auto" }}>
        <img
          src={TifinESALogo}
          alt="Tifin Esa Logo"
          style={{ width: "100%", cursor: "pointer" }}
          onClick={() => goToPage(menuItems[0].link)}
        />
        <div style={{ marginTop: "16px" }}>
          {menuItems.map((item, index) => {
            const isSelected = activePage?.label === item.label;
            return (
              <div key={index} style={{ width: "100%" }}>
                <MenuRow
                  sx={{
                    alignItems: "center",
                    backgroundColor: isSelected ? colors.teal_50 : "inherit",
                  }}
                  onClick={() => goToPage(item.link)}
                >
                  {isSelected && <MenuSelected sx={{ margin: "0 4px 0 0" }} />}
                  <item.icon />
                  <RowLabel>{item.label}</RowLabel>
                </MenuRow>
                {index < menuItems.length && <Divider />}
              </div>
            );
          })}
        </div>
      </SectionFlexColumn>
    </SideNavContainer>
  );
};

export const Header = ({
  height,
  children,
}: {
  height?: string;
  children: React.ReactElement;
}) => {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        height: height ? height : "200px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: isMobile ? "12px" : "32px",
        background:
          "linear-gradient(90.06deg, #DBF2FC 0.06%, #E8E8FF 51.56%, #FFF4E3 99.94%)",
        position: "relative",
      }}
    >
      {children}
    </div>
  );
};

export const MobileHeader = ({
  title,
  showBack,
  handleBack,
  showClose,
  closeRoutePath,
}: {
  title: string;
  showBack: boolean;
  handleBack?: () => void;
  showClose: boolean;
  closeRoutePath?: string;
}) => {
  const navigate = useNavigate();
  return (
    <SectionFlexRow
      sx={{
        height: "60px",
        position: "relative",
        borderBottom: `1px solid ${colors.gray_200}`,
      }}
    >
      <div
        style={{ flex: 1 }}
        onClick={() => (handleBack ? handleBack() : navigate(-1))}
      >
        {showBack && (
          <BackIcon
            style={{ position: "absolute", left: "25px", top: "23px" }}
          />
        )}
      </div>
      <T variant="mdMed" sx={{ flex: 1, textAlign: "center", flexShrink: 0 }}>
        {title}
      </T>
      <div style={{ flex: 1 }}>
        {showClose && (
          <CloseIcon
            style={{ position: "absolute", right: "25px", top: "19px" }}
            onClick={() =>
              navigate(closeRoutePath ? closeRoutePath : employeeAccountPath)
            }
          />
        )}
      </div>
    </SectionFlexRow>
  );
};

export const DesktopHeader = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  return <Header height="88px">{children}</Header>;
};

export const Card = ({
  children,
  sx,
  hideStyleOnMobile = false,
  onClick,
}: {
  children: React.ReactNode;
  sx?: React.CSSProperties;
  hideStyleOnMobile?: boolean;
  onClick?: () => void;
}) => {
  const isMobile = useIsMobile();
  return (
    <div
      onClick={onClick}
      style={
        hideStyleOnMobile && isMobile
          ? { margin: "24px 0 0 0", ...sx }
          : {
              display: "flex",
              flexDirection: "column",
              padding: "16px 16px",
              width: "100%",
              margin: "24px 0 0 0",
              border: `1px solid ${colors.gray_01}`,
              borderRadius: "4px",
              background: `${colors.white}`,
              cursor: onClick ? "pointer" : "default",
              ...sx,
            }
      }
    >
      {children}
    </div>
  );
};

type Title = "Balance" | "Contribution" | "Withdrawals";
export const ChartTitleOption = ({
  title,
  handleClick,
  activePage,
}: {
  title: Title;
  handleClick: () => void;
  activePage: Title;
}) => {
  const isActive = activePage === title;
  return (
    <div
      className="flex items-center"
      style={{
        height: 36,
        borderBottom: isActive
          ? `2px solid ${colors.black}`
          : "2px solid white",
        cursor: "pointer",
      }}
      onClick={handleClick}
    >
      <T
        variant="mdMed"
        color={isActive ? `${colors.black}` : `${colors.gray_600}`}
      >
        {title}
      </T>
    </div>
  );
};

export const AccountChart = ({
  data,
  balance,
}: {
  data: QueryAccountResponse;
  balance: string;
}) => {
  const [activePage, setActivePage] = useState<Title>("Balance");
  const [chartPeriod, setChartPeriod] = useState<ChartTypePeriod>("30d");
  const isMobile = useIsMobile();

  const marks = [
    { value: 10, label: "30D", backendValue: "30d" },
    { value: 50, label: "1Y", backendValue: "1y" },
    { value: 90, label: "All", backendValue: "all" },
  ];

  const formatYAxis = (value: any) => {
    return "";
  };

  const formatXAxis = (value: string) => {
    if (value && !["dataMin", "dataMax", "auto"].includes(value)) {
      const dateFormat =
        chartPeriod === "30d" || (chartPeriod === "1y" && isMobile)
          ? "MMM D"
          : "MMM D, YYYY";
      return formatDate(value, dateFormat);
    }
    return "";
  };

  const handleChartPeriodChange = (event: any, newValue: number | number[]) => {
    const period = marks.find((mark) => mark.value === newValue);
    const backendValue = period?.backendValue as ChartTypePeriod;
    setChartPeriod(backendValue);
  };

  const ChartResponsiveContainer = ({
    children,
  }: {
    children: React.ReactElement;
  }) => {
    return (
      <ResponsiveContainer height={220} width={"100%"}>
        {children}
      </ResponsiveContainer>
    );
  };

  const CustomizedTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0];
      const dateFormat = chartPeriod === "30d" ? "MMM D" : "MMM D, YYYY";
      return (
        <div>
          <T variant="smReg">
            {formatDate(dataPoint.payload.period, dateFormat)}
          </T>
          <T variant="smReg">{formatCurrency(dataPoint.value, 2)}</T>
        </div>
      );
    }
    return null;
  };

  const BalanceChart = () => {
    const { data: chartData } = useGetAccountBalances(chartPeriod, "balances");
    const isMobile = useIsMobile();

    const calculateXAxisInterval = () => {
      if (!chartData?.length) return 0;

      if (chartPeriod === "all") {
        const length = chartData.length;
        return length - 2;
      }

      if (chartPeriod === "30d") {
        return Math.floor(chartData?.length / 4);
      }
      if (chartPeriod === "1y") {
        if (isMobile) {
          return Math.floor(chartData?.length / 4);
        } else {
          // about every other month
          return Math.floor(chartData?.length / 6);
        }
      }
      return 0;
    };

    const CustomizedDot = (props: any) => {
      const today = formatDate(moment(), "YYYY-MM-DD");
      const { cx, cy, payload } = props;

      if (payload.period === today) {
        return <TodayTooltip x={cx - 30} y={cy - 35} />;
      }
      return null;
    };

    return (
      <div className="flex flex-col">
        <T variant="headingSm">{balance}</T>
        {chartData && (
          <ChartResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            >
              <Line
                type="linear"
                dataKey="period_balance"
                stroke="#00B186"
                strokeWidth={2}
                activeDot={{ r: 8 }}
                dot={<CustomizedDot />}
              />
              <YAxis
                type="number"
                interval={0}
                width={20}
                axisLine={false}
                domain={["dataMin", "dataMax"]}
                padding={{ top: 40, bottom: 20 }}
                tickCount={10}
                tickFormatter={formatYAxis}
              />
              <XAxis
                dataKey="period"
                dy={4}
                interval={calculateXAxisInterval()}
                padding={{ left: 20, right: 40 }}
                tickLine={false}
                tick={{ fontSize: 12, fill: colors.black }}
                tickFormatter={formatXAxis}
              />
              <Tooltip content={<CustomizedTooltip />} />
            </LineChart>
          </ChartResponsiveContainer>
        )}
      </div>
    );
  };

  const ContributionChart = () => {
    const { data: contributionData } = useGetAccountBalances(
      chartPeriod,
      "contributions"
    );

    if (contributionData) {
      const data = contributionData[0];
      return (
        <div className="flex flex-col">
          <div className="flex flex-row space-between mb-16">
            <div>
              <T variant="smMed">Your Contributions</T>
              <T variant="headingSm">
                {formatCurrency(data.total_contributions, 2)}
              </T>
            </div>
            <div>
              <T variant="smMed">Employer Matches</T>
              <T variant="headingSm">{formatCurrency(data.total_matches, 2)}</T>
            </div>
          </div>
          <ChartResponsiveContainer>
            <BarChart
              data={contributionData}
              margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
              }}
              barCategoryGap={0}
            >
              <Bar
                dataKey="total_contributions"
                fill={colors.dark_green}
                radius={[10, 10, 0, 0]}
                maxBarSize={1200}
              />
              <Bar
                dataKey="total_matches"
                fill={colors.orange}
                radius={[10, 10, 0, 0]}
                maxBarSize={1200}
              />
            </BarChart>
          </ChartResponsiveContainer>
        </div>
      );
    }
    return null;
  };

  const WithdrawalsChart = () => {
    const { data: withdrawalsData } = useGetAccountBalances(
      chartPeriod,
      "withdrawals"
    );

    if (withdrawalsData) {
      const data = withdrawalsData[0];
      return (
        <div className="flex flex-col">
          <div className="flex flex-row space-between mb-16">
            <div>
              <T variant="smMed">Withdrawals</T>
              <T variant="headingSm">
                {formatCurrency(data.total_withdrawals, 2)}
              </T>
            </div>
            <div>
              <T variant="smMed">Total Contributions</T>
              <T variant="headingSm">
                {formatCurrency(data.total_deposits, 2)}
              </T>
            </div>
          </div>
          <ChartResponsiveContainer>
            <BarChart
              data={withdrawalsData}
              margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
              }}
              barCategoryGap={0}
            >
              <Bar
                dataKey="total_withdrawals"
                fill={colors.magenta}
                radius={[10, 10, 0, 0]}
                maxBarSize={1200}
              />
              <Bar
                dataKey="total_deposits"
                fill={colors.dark_green}
                radius={[10, 10, 0, 0]}
                maxBarSize={1200}
              />
            </BarChart>
          </ChartResponsiveContainer>
        </div>
      );
    }
    return null;
  };

  return (
    <Card sx={{ marginTop: "40px" }}>
      <div className="flex items-center w-100 mb-16">
        <div
          className="flex items-center gap-16"
          style={{
            width: "253px",
            borderBottom: `1px solid ${colors.gray_200}`,
          }}
        >
          <ChartTitleOption
            title="Balance"
            activePage={activePage}
            handleClick={() => setActivePage("Balance")}
          />
          <ChartTitleOption
            title="Contribution"
            activePage={activePage}
            handleClick={() => setActivePage("Contribution")}
          />
          <ChartTitleOption
            title="Withdrawals"
            activePage={activePage}
            handleClick={() => setActivePage("Withdrawals")}
          />
        </div>
      </div>
      <div style={{ width: "100%", alignSelf: "center" }}>
        {activePage === "Balance" && <BalanceChart />}
        {activePage === "Contribution" && <ContributionChart />}
        {activePage === "Withdrawals" && <WithdrawalsChart />}
      </div>
      <div style={{ width: isMobile ? "250px" : "334px", marginTop: "8px" }}>
        <Slider
          aria-label="Custom marks"
          defaultValue={10}
          step={null}
          marks={marks}
          onChange={handleChartPeriodChange}
          sx={{
            color: colors.gray_200,
            "& .MuiSlider-thumb": {
              height: 24,
              width: 40,
              backgroundColor: colors.teal,
              "&:before": {
                display: "none",
              },
              borderRadius: 10,
            },
            "& .MuiSlider-rail": {
              backgroundColor: colors.gray_200,
              opacity: 1,
              height: "8px",
            },
          }}
        />
      </div>
    </Card>
  );
};

export const YourContribution = ({ data }: { data: QueryAccountResponse }) => {
  const navigate = useNavigate();
  const monthlyContributionAmt =
    data.challenger_saving.sponsored_savings.ssp_enrollment
      ?.ssp_ygbm_monthly_contribution || 0;
  return (
    <Card onClick={() => navigate(employeeChangeContributionPath)}>
      <div
        className="flex w-100"
        style={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <T variant="headingSm">Your Contribution</T>
          <T variant="headingMd" display={"inline"}>
            {formatCurrency(monthlyContributionAmt, 2)}{" "}
          </T>
          <T variant="lgReg" display={"inline"}>
            per month
          </T>
        </div>
        <EditIcon />
      </div>
    </Card>
  );
};

const ProgressBar = ({
  goal,
  netContribution,
  progress,
  status,
  startDate,
  endDate,
  parentWidth,
}: {
  goal: string;
  netContribution: string;
  progress: number;
  status: ProgramStatusTypeT;
  startDate: string;
  endDate: string;
  parentWidth: number;
}) => {
  const isMobile = useIsMobile();
  const labelWidth = 59;

  const getFillAmount = () => {
    // we want to show a small amount of fill even if the progress is 0
    if (progress < 10) {
      return isMobile ? 10 : 6;
    } else {
      // we want to cap the progress at 100% if they have saved more than the goal
      return progress > 100 ? 100 : progress;
    }
  };

  const getNetContribution = () => {
    const strippedNetContribution = netContribution
      .replace("$", "")
      .replace(",", "");
    const strippedGoal = goal.replace("$", "").replace(",", "");
    if (Number(strippedNetContribution) > Number(strippedGoal)) {
      return goal;
    }
    return netContribution;
  };

  const getFillColor = () => {
    switch (status) {
      case "complete":
        return colors.blue;
      case "ontrack":
        return colors.dark_green;
      case "offtrack":
      case "missed":
        return colors.dark_red;
      default:
        return colors.dark_green;
    }
  };

  const getLabelMargin = () => {
    const MAX_MARGIN = 85;
    if (progress < 10) {
      return "0%";
    } else if (progress < 100) {
      const labelPosition = parentWidth * (progress / 100) - labelWidth * 0.6;
      return `${labelPosition}px`;
    } else {
      return `${MAX_MARGIN}%`;
    }
  };
  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "8px",
          width: labelWidth,
          minWidth: labelWidth,
          maxWidth: labelWidth,
          marginLeft: getLabelMargin(),
        }}
      >
        <div
          style={{
            height: 41,
            backgroundColor: colors.black,
            borderRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <T variant="mdMed" color={colors.white}>
            {getNetContribution()}
          </T>
        </div>
        <ArrowDown style={{ alignSelf: "center" }} />
      </div>
      <div
        style={{
          width: "100%",
          height: "30px",
          backgroundColor: colors.gray_01,
          borderRadius: "15px",
        }}
      >
        <div
          style={{
            width: `${getFillAmount()}%`,
            height: "30px",
            backgroundColor: getFillColor(),
            borderRadius: "15px",
          }}
        />
      </div>
      <div className="flex flex-col mt-4">
        <div className="flex flex-row space-between">
          <T variant="mdReg">$0</T>
          <T variant="mdReg">{goal}</T>
        </div>
        <div className="flex flex-row space-between">
          <T variant="smReg" color={colors.gray_400}>
            {startDate}
          </T>
          <T variant="smReg" color={colors.gray_400}>
            {endDate}
          </T>
        </div>
      </div>
    </div>
  );
};

const OnTrackCircle = () => {
  return (
    <div
      style={{
        height: 12,
        width: 12,
        borderRadius: 10,
        backgroundColor: colors.dark_green,
        marginRight: 4,
      }}
    />
  );
};

const OffTrackOrMissedCircle = () => {
  return (
    <div
      style={{
        height: 12,
        width: 12,
        borderRadius: 10,
        backgroundColor: colors.dark_red,
        marginRight: 4,
      }}
    />
  );
};

const NotEligibleCircle = () => {
  return (
    <div
      style={{
        height: 12,
        width: 12,
        borderRadius: 10,
        backgroundColor: colors.gray,
        marginRight: 4,
      }}
    />
  );
};

const StatusBadge = ({
  Icon,
  message = "",
}: {
  Icon: React.ReactNode;
  message: string | null;
}) => {
  return (
    <div className="flex items-center">
      {Icon}
      <T variant="smReg" color={colors.gray_600} style={{ marginLeft: "2px" }}>
        {message}
      </T>
    </div>
  );
};

const MatchHeader = ({
  matchAmount,
  title,
  status,
}: {
  matchAmount: string;
  title: string;
  status: ProgramStatusTypeT | "not_eligible" | "earned" | undefined;
}) => {
  let Icon = null;
  let message = null;
  switch (status) {
    case "complete":
      Icon = <EarnedIcon />;
      message = "Complete";
      break;
    case "earned":
      Icon = <EarnedIcon />;
      message = "Earned";
      break;
    case "ontrack":
      Icon = <OnTrackCircle />;
      message = "On Track";
      break;
    case "offtrack":
      Icon = <OffTrackOrMissedCircle />;
      message = "Off Track";
      break;
    case "missed":
      Icon = <OffTrackOrMissedCircle />;
      message = "Missed";
      break;
    case "not_eligible":
      Icon = <NotEligibleCircle />;
      message = "Not Eligible This Month";
      break;
    default:
      Icon = null;
      message = null;
  }
  return (
    <div className="flex flex-row">
      <div
        style={{
          height: "42px",
          width: "42px",
          background: "#7B73E4",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: 2,
        }}
      >
        <T variant="mdMed" color={"white"}>
          {formatCurrency(matchAmount)}
        </T>
      </div>
      <div className="flex flex-col ml-8">
        <T variant="lgMed">{title}</T>
        <StatusBadge Icon={Icon} message={message} />
      </div>
    </div>
  );
};

export const ProgramGoalMatch = ({ data }: { data: SponsoredSavings }) => {
  const ref = useRef(null);
  const { width } = useContainerDimensions(ref);

  if (!data.program || !data.ssp_enrollment) return null;
  const goal = formatCurrency(data.program?.ygbm_goal || "0");
  const reward = formatCurrency(data.program?.ygbm_reward || "0");
  const netContribution =
    data.ssp_enrollment?.sponsored_savings_status
      .current_program_net_contributions;
  const status =
    data.ssp_enrollment?.sponsored_savings_status.current_program_status;
  const minToHitGoal = formatCurrency(
    data.ssp_enrollment?.sponsored_savings_status
      .current_program_min_contribution_to_hit_goal || "0"
  );
  const programStart = formatDate(
    data.ssp_enrollment.sponsored_savings_status
      .current_program_start_timestamp,
    "MMM D, yyyy"
  );
  const programEnd = formatDate(
    data.ssp_enrollment.sponsored_savings_status.next_program_start_timestamp,
    "MMM D, yyyy"
  );
  const progress = calculateGoalProgress(
    netContribution,
    data.program.ygbm_goal
  );
  let message = "";
  switch (status) {
    case "complete":
      message = `Great work! You earned the ${reward} match.`;
      break;
    case "ontrack":
      message = `Continue saving towards your ${goal} balance goal to earn the ${reward} match.`;
      break;
    case "offtrack":
      message = `At this rate you will not reach the ${goal} target balance to earn the match. Increase your contribution to ${minToHitGoal}/month to get on track.`;
      break;
    case "missed":
      let nextStartDate =
        data.ssp_enrollment?.sponsored_savings_status
          .next_program_start_timestamp;
      if (nextStartDate) {
        nextStartDate = formatDate(nextStartDate, "MMM d, yyyy");
      }
      message = `Oh no! It looks like you won't be able to hit the savings goal for the current program. You will be eligible to earn your next match starting on ${nextStartDate}.`;
      break;
    default:
      message = "";
  }
  if (reward) {
    return (
      <div
        ref={ref}
        style={{
          borderBottom: `1px solid ${colors.gray_200}`,
          paddingBottom: "20px",
        }}
      >
        <MatchHeader matchAmount={reward} title="Goal Match" status={status} />
        <T variant="mdReg" sx={{ marginTop: "12px" }}>
          {message}
        </T>
        <div style={{ marginTop: "40px" }}>
          <ProgressBar
            goal={goal}
            netContribution={formatCurrency(netContribution)}
            progress={progress}
            status={status}
            startDate={programStart}
            endDate={programEnd}
            parentWidth={width}
          />
        </div>
      </div>
    );
  }
  return null;
};

const ProgramNoSpendMatch = ({ data }: { data: SponsoredSavings }) => {
  const rawReward = data.program?.no_spend_monthly_match_bonus;
  const reward = formatCurrency(rawReward || "0");
  const matchHistory =
    data.ssp_enrollment?.sponsored_savings_matches
      .no_spend_monthly_match_history || [];

  if (rawReward && Number(rawReward) > 0) {
    return (
      <div
        style={{
          borderBottom: `1px solid ${colors.gray_200}`,
          paddingTop: "20px",
          paddingBottom: "20px",
        }}
      >
        <MatchHeader
          matchAmount={reward}
          title="No Spend Monthly Match"
          status={undefined}
        />
        <T variant="mdReg" sx={{ marginTop: "12px" }}>
          Contribute at least {reward} and don't make any withdrawals this month
          to earn this match.
        </T>
        <div>
          <T variant="mdMed" sx={{ marginTop: "8px" }}>
            Match History
          </T>
          <div className="flex flex-row">
            {matchHistory.length ? (
              matchHistory.map((match, index) => {
                return (
                  <T
                    key={match.month_name}
                    variant="mdReg"
                    color={colors.gray_600}
                    sx={{
                      marginRight: "3px",
                      textDecoration: match.has_payout
                        ? "line-through"
                        : "none",
                    }}
                  >
                    {match.month_name.slice(0, 3)}
                    {index < matchHistory.length - 1 ? "," : ""}
                  </T>
                );
              })
            ) : (
              <T variant="mdReg" color={colors.gray_600}>
                No matches yet
              </T>
            )}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const ProgramSignUpBonusMatch = ({ data }: { data: SponsoredSavings }) => {
  const reward = formatCurrency(
    data.program?.employer_program_sign_up_bonus_amount || "0"
  );
  const isEarned =
    data.ssp_enrollment?.sponsored_savings_matches.earned_sign_up_bonus;
  if (reward) {
    return (
      <div
        style={{
          paddingBottom: "20px",
          paddingTop: "20px",
        }}
      >
        <MatchHeader
          matchAmount={reward}
          title="Sign up Bonus"
          status={isEarned ? "earned" : "missed"}
        />
      </div>
    );
  }
  return null;
};

export const MatchProgress = ({ data }: { data: SponsoredSavings }) => {
  const isEnabled = data.enabled;
  const isEnrolled = isEnrolledInSponsoredSavings(data);
  const monthlyContributionAmt =
    data.ssp_enrollment?.ssp_ygbm_monthly_contribution || 0;
  const program = data.program;

  if (isEnabled && isEnrolled && program) {
    return (
      <Card>
        <div style={{ height: "80px" }}>
          <T variant="headingSm">Match Progress</T>
          <T variant="smReg">
            Saving {formatCurrency(monthlyContributionAmt)}/month
          </T>
        </div>
        <div>
          <ProgramGoalMatch data={data} />
          <ProgramNoSpendMatch data={data} />
          <ProgramSignUpBonusMatch data={data} />
        </div>
      </Card>
    );
  }
  return null;
};

export const RecentActivity = ({
  data,
}: {
  data: QueryAccountResponse["challenger_saving"]["recent_transactions"];
}) => {
  const navigate = useNavigate();
  const hasTransactions = data.length > 0;

  const NoTransactions = () => {
    return (
      <div>
        <T variant="lgReg">Deposit funds to start using TIFIN @Work</T>
        <Link to={EMPLOYEE_TRANSFERS}>
          <T variant="smReg" style={{ marginTop: "8px" }}>
            Add Funds
          </T>
        </Link>
      </div>
    );
  };

  const Transactions = () => {
    return (
      <div>
        {data.map((transaction, i) => {
          return (
            <Transaction
              key={transaction.id}
              transaction={transaction}
              showBorder={i < data.length - 1}
            />
          );
        })}
      </div>
    );
  };

  return (
    <Card onClick={() => navigate(employeeTransactionsPath)}>
      <div style={{ height: "60px" }}>
        <SectionFlexRow style={{ justifyContent: "space-between" }}>
          <SectionFlexColumn>
            <T variant="headingSm">Recent Activity</T>
            {!hasTransactions && <T variant="smReg">No transactions yet</T>}
          </SectionFlexColumn>
          <ArrowRight />
        </SectionFlexRow>
      </div>
      <div>{hasTransactions ? <Transactions /> : <NoTransactions />}</div>
    </Card>
  );
};

export const ReferralCard = ({
  data,
}: {
  data: QueryAccountResponse["employer"];
}) => {
  const navigate = useNavigate();
  if (
    data.coworker_referral_program.feature_flag &&
    data.coworker_referral_program.referral_amount
  ) {
    const bonus = formatCurrency(
      data.coworker_referral_program.referral_amount
    );
    return (
      <Card onClick={() => navigate(employeeAccounInviteCoworkersPath)}>
        <SectionFlexRow style={{ justifyContent: "space-between" }}>
          <div>
            <T variant="headingSm" display={"inline"}>
              Invite Coworkers,{" "}
            </T>
            <T
              variant="headingSm"
              display={"inline"}
              color={colors.light_purple}
            >
              Earn {bonus}
            </T>
          </div>
          <ArrowRight />
        </SectionFlexRow>

        <Button sx={{ width: "84px", marginLeft: 0 }}>Earn {bonus}</Button>
      </Card>
    );
  }
  return null;
};

export const YourDebitCard = ({
  data,
}: {
  data: QueryAccountResponse["challenger_saving"]["debit_card"];
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  if (hasDebitCard(data)) {
    return (
      <Card onClick={() => navigate(employeeDebitCardInfoPath)}>
        <SectionFlexRow style={{ justifyContent: "space-between" }}>
          <div>
            <T variant="headingSm">Your Debit Card</T>
          </div>
          <ArrowRight />
        </SectionFlexRow>
        <div
          className="mt-16 flex items-center w-100 justify-center"
          style={{
            backgroundColor: !isMobile ? colors.gray_100 : "inherit",
            padding: "8px",
          }}
        >
          <DebitCardImg style={{ width: isMobile ? "100%" : "80%" }} />
        </div>
      </Card>
    );
  }
  return null;
};

export const ActivateCard = ({
  data,
}: {
  data: QueryAccountResponse["challenger_saving"]["debit_card"];
}) => {
  const navigate = useNavigate();

  if (hasDebitCard(data) && !cardIsActivated(data)) {
    return (
      <Card
        sx={{ border: `1px solid ${colors.yellow}` }}
        onClick={() => navigate(employeeActivateCardPath)}
      >
        <SectionFlexRow
          style={{ justifyContent: "space-between", marginBottom: 40 }}
        >
          <div>
            <T variant="headingSm">Activate Your Debit Card</T>
          </div>
          <ArrowRight />
        </SectionFlexRow>
        {data.latest_card?.estimated_arrival_date && (
          <SectionFlexRow sx={{ marginBottom: "12px" }}>
            <MailIcon />
            <SectionFlexColumn>
              <T variant="mdMed" sx={{ marginLeft: "8px" }}>
                Estimated Arrival Date
              </T>
              <T
                variant="mdReg"
                sx={{ marginLeft: "8px" }}
                color={colors.gray_600}
              >
                {formatDate(
                  data.latest_card.estimated_arrival_date,
                  "MMM D, YYYY"
                )}
              </T>
            </SectionFlexColumn>
          </SectionFlexRow>
        )}
        <T variant="mdReg">
          Your debit card allows you to quickly access money from your TIFIN
          @Work acount.
        </T>
        <div className="mt-16 flex items-center justify-center w-100">
          <DebitCardImg />
        </div>
      </Card>
    );
  }
  return null;
};

export const FAQs = ({
  data,
}: {
  data: QueryAccountResponse["challenger_saving"]["debit_card"];
}) => {
  return (
    <Card>
      <SectionFlexRow
        style={{ justifyContent: "space-between", marginBottom: 40 }}
      >
        <div>
          <T variant="headingSm">Frequently Asked Questions</T>
        </div>
      </SectionFlexRow>
      <SectionFlexRow
        style={{ justifyContent: "space-between", marginBottom: 16 }}
      >
        <T variant="mdReg" sx={{ width: "90%" }}>
          How do my balance and sponsored savings matches work together?
        </T>
        <ArrowRight style={{ width: "10%" }} />
      </SectionFlexRow>
      {showDebitCardFeature(data) && (
        <SectionFlexRow
          style={{ justifyContent: "space-between", marginBottom: 16 }}
        >
          <T variant="mdReg" sx={{ width: "90%" }}>
            What happens when I spend money on my debit card?
          </T>
          <ArrowRight style={{ width: "10%" }} />
        </SectionFlexRow>
      )}
      <SectionFlexRow
        style={{ justifyContent: "space-between", marginBottom: 40 }}
      >
        <T variant="mdReg" sx={{ width: "90%" }}>
          How do I change my monthly contribution?
        </T>
        <ArrowRight style={{ width: "10%" }} />
      </SectionFlexRow>
    </Card>
  );
};
